// ============================================================================
// PAYMENT API TYPES
// ============================================================================

export interface Payment {
  paymentId: string;
  bookingId: string;
  bookingNumber: string;
  propertyId: string;
  propertyName?: string;
  propertyCode?: string;
  guestId?: string;
  guestFullName?: string;
  guestPhone?: string;
  checkInDate: string;
  checkOutDate: string;
  bookingGrandTotal?: number;
  bookingAdvanceReceived?: number;
  bookingBalanceDue?: number;
  amount: number;
  paymentMode: string;
  paymentDate: string;
  paymentReference?: string;
  bankName?: string;
  chequeNumber?: string;
  paymentType: string;
  paymentStatusId: string;
  paymentStatusCode: string;
  paymentStatus?: string;
  paymentStatusColor?: string;
  originalPaymentId?: string;
  receivingBankAccountId?: string;
  receivingBankAccountCode?: string;
  receivingBankAccountName?: string;
  receivingBankName?: string;
  remarks?: string;
  statusId: string;
  statusCode: string;
  statusName?: string;
  isActive: boolean;
  rowVersion: number;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
  /** Voucher reference Fairbooks gave this payment (or that Accounts keyed). */
  acctTxnRef?: string;
  /** Receipt / refund voucher number issued by CRS (slice 4). */
  receiptNumber?: string | null;
  /** null (before numbering) / ISSUED / PENDING / EXTERNAL. */
  receiptNumberState?: 'ISSUED' | 'PENDING' | 'EXTERNAL' | null;
  /** Which system recorded the money first. */
  origin: PaymentOrigin;
  /** Bank verification state — display only, never gates the booking. */
  verificationStatus: PaymentVerificationStatus;
  verifiedAt?: string;
  verifiedByName?: string;
  bankValueDate?: string;
  verifiedAmount?: number;
  totalCount: number;
}

export type PaymentOrigin = 'CRS' | 'FAIRBOOKS';
/** Where the money was taken (slice 7d). Left out, the server decides from the booking. */
export type CollectionPoint = 'FRONT_OFFICE' | 'RESERVATIONS' | 'ACCOUNTS';
export type PaymentVerificationStatus = 'UNVERIFIED' | 'VERIFIED' | 'DISCREPANCY';

export interface CreatePaymentRequest {
  bookingId: string;
  amount: number;
  paymentMode: string;
  paymentDate: string;
  paymentReference?: string;
  bankName?: string;
  chequeNumber?: string;
  receivingBankAccountId?: string;
  paymentType?: string;
  remarks?: string;
  /** 'FAIRBOOKS' = "Recorded by Accounts": already in Fairbooks, never posted again. */
  origin?: PaymentOrigin;
  /** Fairbooks voucher reference — required when origin is FAIRBOOKS. */
  acctTxnRef?: string;
  /** Date the bank credited the money, when Accounts supplied it (yyyy-MM-dd). */
  bankValueDate?: string;
  /** The screen that records it says where it was taken (Front Office closing, slice 7d). */
  collectionPoint?: CollectionPoint;
}

export interface UpdatePaymentRequest {
  amount: number;
  paymentMode: string;
  paymentDate: string;
  paymentReference?: string;
  bankName?: string;
  chequeNumber?: string;
  receivingBankAccountId?: string;
  remarks?: string;
  rowVersion: number;
}

export interface PaymentFilterParams {
  bookingId?: string;
  propertyId?: string;
  paymentMode?: string;
  paymentType?: string;
  paymentStatusCode?: string;
  receivingBankAccountId?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  includeVoided?: boolean;
}

export interface PaymentSearchRequest extends PaymentFilterParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Payment modes — used in the SyutSelect on the capture modal.
 * Receiving bank account is required for digital modes; optional for Cheque; hidden for Cash.
 */
export const PAYMENT_MODES = ['Cash', 'Credit Card', 'Debit Card', 'UPI', 'NEFT/RTGS', 'Cheque', 'Online'] as const;
export type PaymentMode = typeof PAYMENT_MODES[number];

export const DIGITAL_PAYMENT_MODES = new Set<PaymentMode>([
  'Credit Card', 'Debit Card', 'UPI', 'NEFT/RTGS', 'Online',
]);

export const isReceivingBankAccountRequired = (mode: string): boolean =>
  DIGITAL_PAYMENT_MODES.has(mode as PaymentMode);

export const isReceivingBankAccountVisible = (mode: string): boolean =>
  mode !== 'Cash';
